import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, waitForRender } from '../lib/api';
import { useAuth } from '../lib/auth';
import { describePath, formatValue, setPath } from '../lib/paths';
import type { Change, Comment, Issue, RenderJob, Version } from '../lib/types';
import { Spinner, useToast } from '../components/ui';
import { useProjectContext } from './ProjectLayout';

type EditTarget =
  | { tab: 'content'; section: string; day?: number }
  | { tab: 'design'; slot: string };

const COMMENT_SECTIONS: Array<[string, string]> = [
  ['', 'General'], ['tour', 'Cover & tour info'], ['route', 'Route & map'], ['schedule', 'Schedule'],
  ['days', 'Day pages'], ['experience', 'Experience'], ['packages', 'Packages & prices'],
  ['inclusions', 'Included / not included'], ['deposits', 'Deposits'], ['contact', 'Contact'], ['photos', 'Photos'],
];

export function PreviewPage() {
  const { project, saving, update, canEdit, reload } = useProjectContext();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const frameRef = useRef<HTMLIFrameElement>(null);
  const scrollY = useRef(0);
  const editing = useRef(false);
  const reloadPending = useRef(false);
  const [frameKey, setFrameKey] = useState(0);

  const [job, setJob] = useState<RenderJob | null>(null);
  const [layoutIssues, setLayoutIssues] = useState<Issue[] | null>(null);
  const [checking, setChecking] = useState(false);
  const [pages, setPages] = useState<number | null>(null);
  const [panel, setPanel] = useState<'comments' | 'versions'>('comments');

  const refreshFrame = useCallback(() => {
    if (editing.current) {
      reloadPending.current = true;
      return;
    }
    setFrameKey((k) => k + 1);
  }, []);

  // Messages from the preview: clicks, inline edits, scroll position.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const data = e.data;
      if (!data || typeof data !== 'object') return;
      switch (data.type) {
        case 'wnw-edit': {
          const t = data.target as EditTarget;
          if (!canEdit) return;
          if (t.tab === 'content') navigate(`/projects/${project.id}/content?section=${t.section}${t.day ? `&day=${t.day}` : ''}`);
          else navigate(`/projects/${project.id}/design#slot-${t.slot}`);
          break;
        }
        case 'wnw-inline': {
          if (!canEdit) {
            toast.push('Your role can’t edit text', true);
            refreshFrame();
            return;
          }
          try {
            update((b) => setPath(b, data.path, data.value));
          } catch (err) {
            toast.push((err as Error).message, true);
          }
          break;
        }
        case 'wnw-editing':
          editing.current = !!data.on;
          if (!data.on && reloadPending.current) {
            reloadPending.current = false;
            setFrameKey((k) => k + 1);
          }
          break;
        case 'wnw-refresh':
          break;
        case 'wnw-scrolled':
          scrollY.current = data.y ?? 0;
          break;
        case 'wnw-fit':
          setPages(data.pages ?? null);
          break;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [canEdit, navigate, project.id, refreshFrame, toast, update]);

  // After each save: reload the preview and re-run the layout check.
  const firstSave = useRef(true);
  useEffect(() => {
    if (saving) return;
    if (firstSave.current) {
      firstSave.current = false;
    } else {
      refreshFrame();
    }
    let cancelled = false;
    setChecking(true);
    const t = window.setTimeout(() => {
      api.preflight(project.id, true)
        .then((res) => { if (!cancelled) { setLayoutIssues(res.issues); setPages(res.pages); } })
        .catch(() => {})
        .finally(() => { if (!cancelled) setChecking(false); });
    }, 400);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [saving, project.id, project.templateId]);

  const onFrameLoad = () => {
    const win = frameRef.current?.contentWindow;
    if (win && scrollY.current) win.postMessage({ type: 'wnw-scroll', y: scrollY.current }, '*');
  };

  const showPage = (page: number) => {
    const doc = frameRef.current?.contentDocument;
    const el = doc?.querySelectorAll('.page')[page - 1] as HTMLElement | undefined;
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const render = async (preset: 'web' | 'print') => {
    try {
      const started = await api.startRender(project.id, preset);
      setJob(started);
      const finished = await waitForRender(started.id, setJob);
      setJob(finished);
      if (finished.status === 'failed') toast.push(`PDF failed: ${finished.error}`, true);
      else if (finished.url) window.location.assign(finished.url);
    } catch (e) {
      toast.push((e as Error).message, true);
    }
  };

  const issues = layoutIssues ?? project.issues ?? [];
  const blockers = issues.filter((i) => i.level === 'blocker');
  const warnings = issues.filter((i) => i.level === 'warning');
  const busy = job?.status === 'queued' || job?.status === 'running';
  const pageOf = (i: Issue) => Number(i.message.match(/page (\d+)/)?.[1]) || null;

  return (
    <div className="preview-layout">
      <div>
        <div className="panel">
          <div className="row">
            {can('comment') && (
              <>
                <button className="btn primary" disabled={busy} onClick={() => void render('web')}>
                  {busy && job?.preset === 'web' ? <><Spinner /> Making PDF…</> : 'Download PDF'}
                </button>
                <button className="btn" disabled={busy} onClick={() => void render('print')}>
                  {busy && job?.preset === 'print' ? <><Spinner /> Making PDF…</> : 'PDF for the printer'}
                </button>
              </>
            )}
            <div style={{ flex: 1 }} />
            <span className="sub" style={{ fontSize: 12 }}>
              {pages ? `${pages} pages · ` : ''}
              {canEdit ? 'Click text to change it here · Ctrl+click or click a photo to open its editor' : 'Preview'}
            </span>
          </div>
          {job?.status === 'failed' && <div className="issue blocker" style={{ marginTop: 12 }}>{job.error}</div>}
        </div>

        {(blockers.length > 0 || warnings.length > 0 || checking) && (
          <div className="panel">
            <div className="row" style={{ marginBottom: 8 }}>
              <h2 className="section" style={{ margin: 0 }}>
                {blockers.length ? `${blockers.length} thing${blockers.length === 1 ? '' : 's'} to fix before publishing` : 'Worth a look'}
              </h2>
              {checking && <span className="row sub" style={{ gap: 6 }}><Spinner /> checking the layout…</span>}
            </div>
            <div className="issues">
              {[...blockers, ...warnings].slice(0, 12).map((issue, i) => (
                <div className={`issue ${issue.level}`} key={`${issue.field}-${i}`}>
                  <div style={{ flex: 1 }}>
                    <div>{issue.message}</div>
                    <div className="path">{describePath(issue.field, project.content)}</div>
                  </div>
                  {pageOf(issue) && <button className="btn small ghost" onClick={() => showPage(pageOf(issue)!)}>Show</button>}
                </div>
              ))}
            </div>
          </div>
        )}

        <iframe
          key={frameKey}
          ref={frameRef}
          className="preview-frame"
          title="Booklet preview"
          src={api.previewUrl(project.id, { edit: canEdit })}
          onLoad={onFrameLoad}
        />
      </div>

      <aside className="side-panel">
        <div className="tabs small">
          <button className={panel === 'comments' ? 'active' : ''} onClick={() => setPanel('comments')}>Comments</button>
          <button className={panel === 'versions' ? 'active' : ''} onClick={() => setPanel('versions')}>Publish &amp; versions</button>
        </div>
        {panel === 'comments'
          ? <CommentsPanel projectId={project.id} pages={pages} />
          : <VersionsPanel blockers={blockers.length} checking={checking} onRestored={() => { void reload(); refreshFrame(); }} />}
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------- comments

function CommentsPanel({ projectId, pages }: { projectId: string; pages: number | null }) {
  const { user, can } = useAuth();
  const toast = useToast();
  const [comments, setComments] = useState<Comment[] | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [body, setBody] = useState('');
  const [section, setSection] = useState('');
  const [page, setPage] = useState('');

  const load = useCallback(() => api.listComments(projectId).then(setComments).catch((e) => toast.push(e.message, true)), [projectId, toast]);
  useEffect(() => { void load(); }, [load]);

  const add = async () => {
    try {
      await api.addComment(projectId, { body, fieldPath: section || null, page: page ? Number(page) : null });
      setBody('');
      setPage('');
      await load();
    } catch (e) {
      toast.push((e as Error).message, true);
    }
  };

  if (!comments) return <div className="row"><Spinner /></div>;
  const open = comments.filter((c) => !c.resolved);
  const shown = showResolved ? comments : open;
  const sectionLabel = (id: string | null) => COMMENT_SECTIONS.find(([k]) => k === (id ?? '').split('.')[0])?.[1] ?? id;

  return (
    <div>
      {can('comment') && (
        <div className="comment-form">
          <textarea placeholder="Leave a comment for the team…" value={body} onChange={(e) => setBody(e.target.value)} rows={3} />
          <div className="row" style={{ gap: 6 }}>
            <select className="inline-select" value={section} onChange={(e) => setSection(e.target.value)}>
              {COMMENT_SECTIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
            <input className="inline-select" style={{ width: 70 }} type="number" min={1} max={pages ?? undefined} placeholder="page" value={page} onChange={(e) => setPage(e.target.value)} />
            <div style={{ flex: 1 }} />
            <button className="btn small primary" disabled={!body.trim()} onClick={() => void add()}>Comment</button>
          </div>
        </div>
      )}

      <div className="row" style={{ margin: '12px 0 6px' }}>
        <span className="sub">{open.length} open</span>
        <div style={{ flex: 1 }} />
        {comments.length > open.length && (
          <button className="btn small ghost" onClick={() => setShowResolved((v) => !v)}>
            {showResolved ? 'Hide resolved' : `Show resolved (${comments.length - open.length})`}
          </button>
        )}
      </div>

      {!shown.length && <div className="sub" style={{ padding: '10px 0' }}>No comments yet.</div>}
      {[...shown].reverse().map((c) => (
        <div className={`comment ${c.resolved ? 'resolved' : ''}`} key={c.id}>
          <div className="row" style={{ gap: 6 }}>
            <b>{c.authorName}</b>
            <span className="sub" style={{ fontSize: 11 }}>{new Date(c.createdAt).toLocaleString()}</span>
          </div>
          {(c.fieldPath || c.page) && (
            <div className="comment-where">
              {c.fieldPath ? sectionLabel(c.fieldPath) : ''}{c.fieldPath && c.page ? ' · ' : ''}{c.page ? `page ${c.page}` : ''}
            </div>
          )}
          <div className="comment-body">{c.body}</div>
          {can('comment') && (
            <div className="row" style={{ gap: 6 }}>
              <button className="btn small ghost" onClick={() => void api.resolveComment(projectId, c.id, !c.resolved).then(load)}>
                {c.resolved ? 'Reopen' : '✓ Resolve'}
              </button>
              {(c.authorId === user?.id || can('admin')) && (
                <button className="btn small ghost" onClick={() => void api.deleteComment(projectId, c.id).then(load)}>Delete</button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- versions

function VersionsPanel({ blockers, checking, onRestored }: { blockers: number; checking: boolean; onRestored: () => void }) {
  const { project, canEdit } = useProjectContext();
  const { can } = useAuth();
  const toast = useToast();
  const [versions, setVersions] = useState<Version[] | null>(null);
  const [label, setLabel] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [diff, setDiff] = useState<{ id: string; changes: Change[] } | null>(null);

  const load = useCallback(() => api.listVersions(project.id).then(setVersions).catch(() => setVersions([])), [project.id]);
  useEffect(() => { void load(); }, [load]);

  const publish = async () => {
    setPublishing(true);
    try {
      const version = await api.publish(project.id, label);
      toast.push(`Published version ${version.number}`);
      setLabel('');
      await load();
      onRestored();
    } catch (e) {
      const err = e as Error & { data?: { issues?: Issue[] } };
      const first = err.data?.issues?.find((i) => i.level === 'blocker');
      toast.push(first ? `${err.message}: ${first.message}` : err.message, true);
    } finally {
      setPublishing(false);
    }
  };

  const compare = async (v: Version) => {
    if (diff?.id === v.id) return setDiff(null);
    try {
      const res = await api.versionDiff(project.id, v.id);
      setDiff({ id: v.id, changes: res.changes });
    } catch (e) {
      toast.push((e as Error).message, true);
    }
  };

  const restore = async (v: Version) => {
    if (!window.confirm(`Replace the current text, prices and photos with version ${v.number}? You can undo this by restoring again from History.`)) return;
    try {
      await api.restoreVersion(project.id, v.id);
      toast.push(`Version ${v.number} restored`);
      onRestored();
    } catch (e) {
      toast.push((e as Error).message, true);
    }
  };

  const share = async (v: Version) => {
    try {
      const { path } = await api.shareVersion(project.id, v.id);
      const url = `${window.location.origin}${path}`;
      await navigator.clipboard?.writeText(url).catch(() => {});
      toast.push('Share link copied');
      await load();
      return url;
    } catch (e) {
      toast.push((e as Error).message, true);
      return null;
    }
  };

  const shareUrl = (v: Version) => (v.shareToken ? `${window.location.origin}/share/${v.shareToken}` : null);

  return (
    <div>
      {can('review') ? (
        <div className="publish-box">
          <div className="sub" style={{ marginBottom: 8, fontSize: 12 }}>
            Publishing makes both PDFs and freezes this version. Later edits go into the next version.
          </div>
          <input className="search" style={{ width: '100%', marginBottom: 8 }} placeholder="Label (optional) — e.g. Sent to clients" value={label} onChange={(e) => setLabel(e.target.value)} />
          <button className="btn primary" style={{ width: '100%', justifyContent: 'center' }} disabled={publishing || blockers > 0 || checking} onClick={() => void publish()}>
            {publishing ? <><Spinner /> Publishing…</> : `Publish version ${(versions?.length ?? 0) + 1}`}
          </button>
          {blockers > 0 && <div className="sub" style={{ marginTop: 6, fontSize: 12 }}>Fix the {blockers} item{blockers === 1 ? '' : 's'} listed on the left first.</div>}
        </div>
      ) : (
        <div className="sub" style={{ fontSize: 12, marginBottom: 10 }}>A reviewer or admin publishes booklets. Send it for review from the top bar.</div>
      )}

      {!versions && <Spinner />}
      {versions && !versions.length && <div className="sub" style={{ padding: '12px 0' }}>Nothing published yet.</div>}
      {versions?.map((v) => (
        <div className="version" key={v.id}>
          <div className="row" style={{ gap: 6 }}>
            <b className="mono">v{v.number}</b>
            <span style={{ flex: 1 }}>{v.label || <span className="sub">no label</span>}</span>
          </div>
          <div className="sub" style={{ fontSize: 11 }}>{new Date(v.publishedAt).toLocaleString()} · {v.publishedBy}</div>
          <div className="row" style={{ gap: 5, marginTop: 6 }}>
            {v.pdfWeb && <a className="btn small" href={v.pdfWeb} download>PDF</a>}
            {v.pdfPrint && <a className="btn small" href={v.pdfPrint} download>Print PDF</a>}
            <button className="btn small ghost" onClick={() => void compare(v)}>{diff?.id === v.id ? 'Hide changes' : 'Compare'}</button>
            {canEdit && <button className="btn small ghost" onClick={() => void restore(v)}>Restore</button>}
          </div>

          {can('comment') && (
            <div className="share-row">
              {shareUrl(v) ? (
                <>
                  <input readOnly value={shareUrl(v)!} onFocus={(e) => e.target.select()} />
                  <div className="row" style={{ gap: 5 }}>
                    <button className="btn small" onClick={() => void navigator.clipboard?.writeText(shareUrl(v)!).then(() => toast.push('Link copied'))}>Copy</button>
                    <a className="btn small" target="_blank" rel="noreferrer" href={`https://wa.me/?text=${encodeURIComponent(`${project.name} — booklet: ${shareUrl(v)}`)}`}>WhatsApp</a>
                    <a className="btn small" href={`mailto:?subject=${encodeURIComponent(project.name)}&body=${encodeURIComponent(`Here is the booklet: ${shareUrl(v)}`)}`}>Email</a>
                    <button className="btn small ghost" onClick={() => void api.unshareVersion(project.id, v.id).then(load).then(() => toast.push('Link switched off'))}>Stop sharing</button>
                  </div>
                </>
              ) : (
                <button className="btn small ghost" onClick={() => void share(v)}>🔗 Create share link</button>
              )}
            </div>
          )}

          {diff?.id === v.id && (
            diff.changes.length ? (
              <table className="table plain changes compact">
                <thead><tr><th>Field</th><th>v{v.number}</th><th>Now</th></tr></thead>
                <tbody>
                  {diff.changes.slice(0, 60).map((c, i) => (
                    <tr key={i}>
                      <td>{describePath(c.path, project.content)}</td>
                      <td className="before">{formatValue(c.from)}</td>
                      <td className="after">{formatValue(c.to)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="sub" style={{ fontSize: 12, marginTop: 6 }}>No changes since this version.</div>
          )}
        </div>
      ))}
    </div>
  );
}
